import { useState, useEffect, useRef, Component, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { SocialPreviewMockup } from "@/components/social-preview-mockup";
import {
  Loader2,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Lock,
  Sparkles,
  Target,
  BarChart2,
  PenTool,
  Users,
  MessageSquare,
  RefreshCw,
  Rocket,
  Zap,
  Brain,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";

// Error boundary to prevent white-screen crashes
class WizardErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; errorMsg: string }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMsg: "" };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, errorMsg: String(error?.message || error) };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-8">
          <div className="max-w-md text-center space-y-4">
            <AlertTriangle size={48} className="text-amber-400 mx-auto" />
            <h2 className="text-xl font-bold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">{this.state.errorMsg}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-6 py-3 text-sm flex items-center gap-2 mx-auto transition-colors"
            >
              <RotateCcw size={14} /> Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

type WizardStep = "research" | "style" | "strategy" | "generating_samples" | "samples" | "locked";

interface StyleVariant {
  style: "bold" | "clean" | "warm";
  caption: string;
  headline: string;
  hashtags: string[];
  imageDataUri?: string;
}

interface SamplePost {
  platform: string;
  caption: string;
  mediaUrl?: string;
  hashtags?: string[];
}

interface SampleAd {
  name: string;
  headline: string;
  bodyText: string;
  imageUrl?: string;
  platform: string;
}

interface Plan {
  id: number;
  wizardStep: WizardStep;
  summary: string;
  competitors: string[];
  swot: { strengths: string[]; weaknesses: string[]; opportunities: string[]; threats: string[] };
  structuredStrategy: any;
  samplePosts: SamplePost[] | null;
  sampleAds: SampleAd[] | null;
  lockedAt: string | null;
}

function mapPlatformToFormat(platform: string): "ig_feed" | "ig_reel" | "fb_feed" | "x_post" | "li_post" {
  const p = platform.toLowerCase();
  if (p.includes("instagram") || p.includes("ig")) return "ig_feed";
  if (p.includes("facebook") || p.includes("meta") || p.includes("fb")) return "fb_feed";
  if (p.includes("linkedin")) return "li_post";
  if (p.includes("x") || p.includes("twitter")) return "x_post";
  return "ig_feed";
}

function ProgressDots({ step }: { step: WizardStep }) {
  const steps: WizardStep[] = ["research", "style", "strategy", "samples", "locked"];
  const labels = ["Research", "Style", "Strategy", "Samples", "Launch"];
  const currentIdx = steps.indexOf(step === "generating_samples" ? "samples" : step);

  return (
    <div className="flex items-center gap-2" data-testid="wizard-progress">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold transition-all ${
            i < currentIdx ? "bg-green-500 text-white" :
            i === currentIdx ? "bg-blue-500 text-white ring-2 ring-blue-500/30" :
            "bg-muted text-muted-foreground/50"
          }`}>
            {i < currentIdx ? <CheckCircle2 size={12} /> : i + 1}
          </div>
          <span className={`text-[10px] font-medium hidden sm:block ${i === currentIdx ? "text-foreground" : "text-muted-foreground/50"}`}>
            {labels[i]}
          </span>
          {i < steps.length - 1 && <div className={`w-8 h-px ${i < currentIdx ? "bg-green-500/50" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

function AgentLogLine({ log }: { log: { type: string; agent: string; content?: string; action?: string } }) {
  const colors: Record<string, string> = {
    milestone: "text-green-400",
    error: "text-red-400",
    warning: "text-amber-300",
    thought: "text-blue-300",
    reasoning: "text-purple-300",
    tool_call: "text-amber-300",
  };
  const agentColors: Record<string, string> = {
    Draper: "text-blue-400",
    Peggy: "text-pink-400",
    Pete: "text-amber-400",
    Rogers: "text-green-400",
  };
  return (
    <div className="flex items-start gap-2 py-1 border-b border-white/5 last:border-0">
      <span className={`text-[9px] font-bold uppercase shrink-0 mt-0.5 ${agentColors[log.agent] || "text-muted-foreground"}`}>
        {log.agent}
      </span>
      <span className={`text-[10px] leading-snug ${colors[log.type] || "text-foreground/70"}`}>
        {(log.content || log.action || '').slice(0, 200)}
      </span>
    </div>
  );
}

function DeepPlanInner() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activePlanId, setActivePlanId] = useState<number | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [styleVariants, setStyleVariants] = useState<StyleVariant[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [researchError, setResearchError] = useState<string | null>(null);
  // non-fatal notes (a tool failed, the run carried on): shown small, never as the "Research failed" banner
  const [researchWarning, setResearchWarning] = useState<string | null>(null);
  // another Draper run (onboarding's orchestration) holds the workspace: follow it, then start the research when it ends
  const [followingRun, setFollowingRun] = useState<{ sessionId: string; kind: string } | null>(null);
  const [advancingToStrategy, setAdvancingToStrategy] = useState(false);
  const logsRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const activePlanIdRef = useRef<number | null>(null);

  const { data: workspace } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId],
    enabled: !!activeWorkspaceId,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const { data: planStatus, refetch: refetchPlan } = useQuery<{ plan: Plan | null }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "deep-plan-status"],
    enabled: !!activeWorkspaceId,
    queryFn: async () => {
      const planId = activePlanIdRef.current;
      const url = planId
        ? `/api/workspaces/${activeWorkspaceId}/deep-plan/status?planId=${planId}`
        : `/api/workspaces/${activeWorkspaceId}/deep-plan/status`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    refetchInterval: 5000,
  });

  const plan = planStatus?.plan;
  const wizardStep = plan?.wizardStep || "research";

  // Show locked success screen when already locked (don't auto-redirect)
  useEffect(() => {
    if (plan?.wizardStep === "locked") {
      setPlanLocked(true);
    }
  }, [plan?.wizardStep]);

  // Load locked style
  useEffect(() => {
    if (workspace?.preferredContentStyle) {
      setSelectedStyle(workspace.preferredContentStyle);
    }
  }, [workspace]);

  // SSE listener for agent logs
  useEffect(() => {
    if (!activeWorkspaceId || !sessionId) return;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const es = new EventSource(`/api/workspaces/${activeWorkspaceId}/stream`);
    eventSourceRef.current = es;
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "connected") return;
        setLogs(prev => [...prev.slice(-50), data]);
        if (data.wizardStep) {
          setResearchError(null);
          // If the milestone includes the planId, pin it so polling tracks the right plan
          if (data.planId && !activePlanIdRef.current) {
            setActivePlanId(data.planId);
            activePlanIdRef.current = data.planId;
          }
          refetchPlan();
        }
        // A tool that failed mid-run is a warning (the run continues): a small inline note.
        if (data.type === "warning" && (!data.sessionId || data.sessionId === sessionId)) {
          setResearchWarning(String(data.content || data.action || "A step had a problem; Draper carried on.").slice(0, 200));
        }
        // Only a run that actually failed (the server marks it fatal, for THIS research session) shows the failure banner.
        if (data.type === "error" && data.fatal && data.sessionId === sessionId) {
          setResearchError(data.content || "Research failed.");
        }
      } catch {}
    };
    return () => es.close();
  }, [activeWorkspaceId, sessionId]);

  // Auto scroll logs
  useEffect(() => {
    if (logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [logs]);

  // POST deep-plan/start. A 409 DRAPER_BUSY is not an error: Draper is already running on this workspace (onboarding's run) — follow it.
  const postDeepPlanStart = async (): Promise<any> => {
    const res = await fetch(`/api/workspaces/${activeWorkspaceId}/deep-plan/start`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 409 && body?.code === "DRAPER_BUSY") return { busy: true, ...body };
    if (!res.ok) throw new Error(body?.message || `${res.status}: ${res.statusText}`);
    return body;
  };

  const onResearchStarted = (data: any) => {
    if (data?.busy) {
      setFollowingRun({ sessionId: data.activeSessionId, kind: data.activeKind });
      setSessionId(data.activeSessionId); // opens the stream so the followed run's progress shows
      setResearchError(null);
      return;
    }
    setFollowingRun(null);
    setSessionId(data.sessionId);
    if (data.planId) {
      setActivePlanId(data.planId);
      activePlanIdRef.current = data.planId;
    }
    setLogs([]);
    setResearchError(null);
    setResearchWarning(null);
    refetchPlan();
  };

  const startResearch = useMutation({
    mutationFn: postDeepPlanStart,
    onSuccess: onResearchStarted,
    onError: (e: any) => toast({ title: "Research failed", description: e.message, variant: "destructive" }),
  });

  // While following another run, try to start the research every 15 s; it starts as soon as that run has finished.
  useEffect(() => {
    if (!followingRun || !activeWorkspaceId) return;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const data = await postDeepPlanStart();
        if (!stopped) onResearchStarted(data);
      } catch { /* keep following; a real failure shows on the next manual try */ }
    }, 15000);
    return () => { stopped = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followingRun?.sessionId, activeWorkspaceId]);

  const generateStylesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/style-lab/generate`, {});
      return res.json();
    },
    onSuccess: (data) => {
      setStyleVariants(data.variants || []);
    },
    onError: (e: any) => toast({ title: "Style generation failed", description: e.message, variant: "destructive" }),
  });

  const lockStyleMutation = useMutation({
    mutationFn: async (style: string) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/style-lab/lock`, { style });
      return res.json();
    },
    onSuccess: (data) => {
      setSelectedStyle(data.lockedStyle);
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId] });
    },
    onError: (e: any) => toast({ title: "Failed to lock style", description: e.message, variant: "destructive" }),
  });

  const resetWizard = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/start?reset=true`, {});
      return res.json();
    },
    onSuccess: (data) => {
      setSessionId(data.sessionId);
      if (data.planId) {
        setActivePlanId(data.planId);
        activePlanIdRef.current = data.planId;
      }
      setLogs([]);
      setResearchError(null);
      refetchPlan();
      toast({ title: "Wizard reset", description: "You can start fresh research." });
    },
    onError: (e: any) => String(e?.message || "").startsWith("409")
      ? toast({ title: "Draper is busy", description: "Draper is already working on this — following that run. Try the reset once it finishes." })
      : toast({ title: "Reset failed", description: e.message, variant: "destructive" }),
  });

  const generateSamples = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/generate-samples`, {});
      return res.json();
    },
    onSuccess: (data) => {
      setSessionId(data.sessionId);
      refetchPlan();
    },
    onError: (e: any) => toast({ title: "Sample generation failed", description: e.message, variant: "destructive" }),
  });

  const submitFeedback = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/feedback`, { feedback: feedbackText });
      return res.json();
    },
    onSuccess: () => {
      setFeedbackText("");
      setShowFeedback(false);
      refetchPlan();
      toast({ title: "Strategy refined", description: "Draper has updated your strategy." });
    },
    onError: (e: any) => toast({ title: "Feedback failed", description: e.message, variant: "destructive" }),
  });

  const [planLocked, setPlanLocked] = useState(false);

  const lockPlan = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/lock`, {
        planId: activePlanIdRef.current,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Strategy locked!", description: "Your brand foundation is set. Now run your agents to fill your content calendar." });
      setPlanLocked(true);
    },
    onError: (e: any) => toast({ title: "Lock failed", description: e.message, variant: "destructive" }),
  });

  const runAgentsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/orchestrate`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Agents started!", description: "Your AI team is filling your content calendar. Watch the agent stream for live updates." });
      navigate("/strategy");
    },
    onError: (e: any) => {
      if (String(e?.message || "").startsWith("409")) {
        // one Draper run per workspace: the one already going is followed on the Strategy page
        toast({ title: "Draper is already working", description: "Draper is already working on this — following that run." });
        navigate("/strategy");
        return;
      }
      toast({ title: "Failed to start agents", description: e.message, variant: "destructive" });
    },
  });

  const businessName = workspace?.brandName || workspace?.name || "Your Business";
  const industry = workspace?.industry || "business";

  const STYLE_COLORS: Record<string, { gradient: string; label: string; desc: string }> = {
    bold: { gradient: "from-red-500/20 to-orange-500/10 border-red-500/30", label: "Bold", desc: "Punchy, urgent, benefit-driven" },
    clean: { gradient: "from-sky-500/20 to-blue-500/10 border-sky-500/30", label: "Clean", desc: "Minimal, professional, no emojis" },
    warm: { gradient: "from-amber-500/20 to-yellow-500/10 border-amber-500/30", label: "Warm", desc: "Conversational, personal, relatable" },
  };

  if (planLocked) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6" data-testid="plan-locked-screen">
        <div className="max-w-lg w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto">
            <Rocket size={36} className="text-emerald-400" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-display font-bold" data-testid="locked-title">Strategy Locked</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Your brand foundation is set for <span className="font-semibold text-foreground">{businessName}</span>.
              Now let your AI team take over — they'll fill your content calendar, build campaigns, and start driving results.
            </p>
          </div>
          <div className="space-y-3">
            <button
              data-testid="button-run-agents"
              onClick={() => runAgentsMutation.mutate()}
              disabled={runAgentsMutation.isPending}
              className="w-full flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-colors disabled:opacity-60"
            >
              {runAgentsMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
              Run Your Agents
            </button>
            <button
              data-testid="button-go-to-strategy"
              onClick={() => navigate("/strategy")}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 text-sm font-medium transition-colors"
            >
              View Strategy
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Rocket size={20} className="text-blue-400" />
              <h1 className="text-xl font-display font-bold" data-testid="page-title">Launch Your Strategy</h1>
            </div>
            <p className="text-sm text-muted-foreground">{businessName} · {industry}</p>
          </div>
          <div className="flex items-center gap-3">
            {plan && (
              <button
                data-testid="button-reset-wizard"
                onClick={() => resetWizard.mutate()}
                disabled={resetWizard.isPending}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-50"
              >
                {resetWizard.isPending ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                Reset Wizard
              </button>
            )}
            <ProgressDots step={wizardStep as WizardStep} />
          </div>
        </div>

        {/* Step 1: Research */}
        {(wizardStep === "research" || !plan) && (
          <div className="space-y-6" data-testid="step-research">
            <div className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Brain size={18} className="text-blue-400" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-lg">Deep Research</h2>
                  <p className="text-sm text-muted-foreground">Draper will research your industry, competitors, and market to build a real marketing plan</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: <Target size={14} />, label: "Competitor Analysis" },
                  { icon: <BarChart2 size={14} />, label: "Industry Insights" },
                  { icon: <Users size={14} />, label: "Audience Profile" },
                  { icon: <Zap size={14} />, label: "Content Opportunities" },
                ].map(({ icon, label }) => (
                  <div key={label} className="flex items-center gap-2 p-2.5 rounded-xl bg-muted/30 border border-border">
                    <span className="text-blue-400">{icon}</span>
                    <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>

              {startResearch.isPending ? (
                <div className="space-y-3">
                  <div
                    ref={logsRef}
                    className="h-40 overflow-y-auto custom-scrollbar bg-black/40 rounded-xl border border-border p-3 space-y-0.5 font-mono text-[10px]"
                    data-testid="research-log-stream"
                  >
                    {logs.length === 0 ? (
                      <div className="flex items-center gap-2 text-blue-400">
                        <Loader2 size={10} className="animate-spin" />
                        <span>Draper is starting research...</span>
                      </div>
                    ) : (
                      logs.map((log, i) => <AgentLogLine key={i} log={log} />)
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground/70 text-center">
                    This takes 60–90 seconds. Draper is searching your industry and competitors...
                  </p>
                </div>
              ) : sessionId && (plan?.wizardStep === "research" || followingRun) ? (
                <div className="space-y-3">
                  {followingRun && (
                    <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center gap-2" data-testid="research-following">
                      <Loader2 size={12} className="animate-spin text-blue-400 shrink-0" />
                      <p className="text-[11px] text-blue-300">
                        Draper is already working on this — following that run. Deep research starts automatically when it finishes.
                      </p>
                    </div>
                  )}
                  <div
                    ref={logsRef}
                    className="h-40 overflow-y-auto custom-scrollbar bg-black/40 rounded-xl border border-border p-3 font-mono text-[10px]"
                    data-testid="research-log-stream"
                  >
                    {logs.map((log, i) => <AgentLogLine key={i} log={log} />)}
                    {!researchError && (
                      <div className="flex items-center gap-2 text-blue-400 mt-1">
                        <Loader2 size={10} className="animate-spin" />
                        <span>{followingRun ? "Following Draper's current run..." : "Researching..."}</span>
                      </div>
                    )}
                  </div>
                  {researchWarning && !researchError && (
                    <p className="text-[10px] text-amber-300/80 flex items-center gap-1.5" data-testid="research-warning">
                      <AlertTriangle size={10} className="shrink-0" /> Note: {researchWarning} (Draper is carrying on.)
                    </p>
                  )}
                  {researchError && (
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-2" data-testid="research-error">
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                        <p className="text-xs text-amber-300 font-medium">{researchError}</p>
                      </div>
                      <button
                        data-testid="button-try-again"
                        onClick={() => { setResearchError(null); startResearch.mutate(); }}
                        className="flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 font-semibold transition-colors"
                      >
                        <RefreshCw size={12} /> Try Again
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  data-testid="button-start-research"
                  onClick={() => startResearch.mutate()}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Brain size={16} />
                  Start Deep Research
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Style Selection */}
        {wizardStep === "style" && (
          <div className="space-y-6" data-testid="step-style">
            <div className="rounded-2xl border border-border bg-card/30 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <PenTool size={18} className="text-purple-400" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-lg">Choose Your Brand Voice</h2>
                  <p className="text-sm text-muted-foreground">Draper has reviewed your brand. Now pick the content style that matches your personality.</p>
                </div>
              </div>

              {selectedStyle ? (
                <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-green-400" />
                  <span className="text-sm text-green-400 font-medium">
                    {selectedStyle.charAt(0).toUpperCase() + selectedStyle.slice(1)} style selected
                  </span>
                </div>
              ) : null}

              {styleVariants.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Generate 3 style variants so you can see how each one would look for {businessName}.</p>
                  <button
                    data-testid="button-generate-styles"
                    onClick={() => generateStylesMutation.mutate()}
                    disabled={generateStylesMutation.isPending}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  >
                    {generateStylesMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {generateStylesMutation.isPending ? "Generating styles (~20s)..." : "Generate Style Samples"}
                  </button>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-3">
                  {styleVariants.map((variant) => {
                    const config = STYLE_COLORS[variant.style];
                    const isSelected = selectedStyle === variant.style;
                    return (
                      <div
                        key={variant.style}
                        data-testid={`style-card-${variant.style}`}
                        className={`rounded-xl border bg-gradient-to-b p-4 space-y-3 cursor-pointer transition-all ${config.gradient} ${isSelected ? "ring-2 ring-blue-500" : "hover:opacity-90"}`}
                        onClick={() => lockStyleMutation.mutate(variant.style)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-wider">{config.label}</span>
                          {isSelected && <CheckCircle2 size={14} className="text-green-400" />}
                        </div>
                        <p className="text-[10px] text-muted-foreground">{config.desc}</p>
                        <div className="w-full overflow-hidden rounded-lg" style={{ height: "100px" }}>
                          <div style={{ transform: "scale(0.4)", transformOrigin: "top left", width: "250%", height: "250%" }}>
                            <SocialPreviewMockup
                              imageUrl={variant.imageDataUri || undefined}
                              caption={variant.caption}
                              platform="ig_feed"
                              username={businessName}
                              showPlatformToggle={false}
                            />
                          </div>
                        </div>
                        <p className="text-[9px] text-muted-foreground/80 line-clamp-2">{variant.caption.slice(0, 80)}</p>
                        {lockStyleMutation.isPending && lockStyleMutation.variables === variant.style ? (
                          <div className="w-full py-1.5 bg-muted rounded-lg flex items-center justify-center gap-1.5">
                            <Loader2 size={10} className="animate-spin text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">Locking...</span>
                          </div>
                        ) : (
                          <button
                            data-testid={`button-lock-style-${variant.style}`}
                            className={`w-full py-1.5 rounded-lg text-[11px] font-bold transition-colors ${isSelected ? "bg-green-500/20 text-green-400" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                          >
                            {isSelected ? "✓ Selected" : "Select This Style"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedStyle && (
                <button
                  data-testid="button-continue-to-strategy"
                  disabled={advancingToStrategy}
                  onClick={async () => {
                    setAdvancingToStrategy(true);
                    try {
                      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/advance-to-strategy`, {});
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({}));
                        throw new Error(err.message || "Failed to advance");
                      }
                      await queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "deep-plan-status"] });
                      await refetchPlan();
                    } catch (e: any) {
                      toast({ title: "Could not advance wizard", description: e.message, variant: "destructive" });
                    } finally {
                      setAdvancingToStrategy(false);
                    }
                  }}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  {advancingToStrategy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {advancingToStrategy ? "Advancing..." : "Continue to Strategy Review"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Strategy Review */}
        {wizardStep === "strategy" && plan && (
          <div className="space-y-6" data-testid="step-strategy">
            <div className="rounded-2xl border border-border bg-card/30 p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Target size={18} className="text-green-400" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-lg">Your Marketing Strategy</h2>
                  <p className="text-sm text-muted-foreground">Review Draper's plan. Refine if needed, then generate sample posts.</p>
                </div>
              </div>

              {!plan.structuredStrategy && (
                <div className="p-5 rounded-xl bg-yellow-500/10 border border-yellow-500/20 space-y-3" data-testid="strategy-not-ready">
                  <p className="text-sm font-semibold text-yellow-400">Strategy generation didn't complete</p>
                  <p className="text-xs text-muted-foreground">Draper ran out of time before finishing your strategy. Please restart the deep plan so it can complete the research and save your strategy.</p>
                  <button
                    data-testid="button-restart-deep-plan"
                    onClick={async () => {
                      try {
                        await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/reset`, {});
                        await refetchPlan();
                      } catch {
                        await refetchPlan();
                      }
                    }}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg text-xs font-semibold transition-colors"
                  >
                    Restart Deep Plan
                  </button>
                </div>
              )}

              {plan.structuredStrategy?.overview && (
                <div className="p-4 rounded-xl bg-muted/30 border border-border space-y-1">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Overview</p>
                  <p className="text-sm text-foreground/80">{plan.structuredStrategy.overview}</p>
                </div>
              )}

              {plan.competitors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Competitors Identified</p>
                  <div className="flex flex-wrap gap-1.5">
                    {plan.competitors.map((c) => (
                      <span key={c} className="px-2.5 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-[10px] text-red-400 font-medium">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {plan.structuredStrategy?.platforms?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Platform Strategy</p>
                  <div className="grid grid-cols-2 gap-2">
                    {plan.structuredStrategy.platforms.slice(0, 4).map((p: any) => (
                      <div key={p.platform} className="p-3 rounded-xl bg-muted/30 border border-border">
                        <p className="text-[11px] font-bold text-foreground">{p.platform}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{p.postingFrequency || p.frequency || "3x/week"}</p>
                        {p.primaryObjective && <p className="text-[9px] text-muted-foreground/60 mt-1">{p.primaryObjective.slice(0, 60)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {plan.swot && (
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(plan.swot).map(([key, items]) => (
                    <div key={key} className="p-3 rounded-xl bg-muted/30 border border-border">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1.5">{key}</p>
                      <ul className="space-y-0.5">
                        {(items as string[]).slice(0, 2).map((item, i) => (
                          <li key={i} className="text-[9px] text-muted-foreground/70 flex items-start gap-1">
                            <span className="mt-0.5">·</span>
                            <span>{item.slice(0, 50)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {showFeedback ? (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Refine with Draper</p>
                  <textarea
                    data-testid="input-strategy-feedback"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="e.g. Focus more on Instagram Reels, less on LinkedIn. Add a pizza deal promotion angle..."
                    rows={3}
                    className="w-full bg-card/50 border border-border focus:border-blue-500 outline-none rounded-xl px-3 py-2.5 text-sm text-foreground/80 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFeedback(false)}
                      className="px-3 py-1.5 border border-border text-muted-foreground rounded-lg text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      data-testid="button-submit-feedback"
                      onClick={() => submitFeedback.mutate()}
                      disabled={!feedbackText.trim() || submitFeedback.isPending}
                      className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                    >
                      {submitFeedback.isPending ? <Loader2 size={12} className="animate-spin" /> : <MessageSquare size={12} />}
                      Refine Strategy
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="button-open-feedback"
                  onClick={() => setShowFeedback(true)}
                  className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <MessageSquare size={14} />
                  Refine with Draper
                </button>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  data-testid="button-generate-samples"
                  onClick={() => generateSamples.mutate()}
                  disabled={generateSamples.isPending}
                  className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {generateSamples.isPending ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {generateSamples.isPending ? "Generating samples..." : "Generate Sample Posts & Ads →"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generating samples loading state */}
        {wizardStep === "generating_samples" && (
          <div className="rounded-2xl border border-border bg-card/30 p-8 space-y-4 text-center" data-testid="step-generating-samples">
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Loader2 size={20} className="text-purple-400 animate-spin" />
              </div>
            </div>
            <h2 className="font-display font-bold text-lg">Creating Your Samples</h2>
            <p className="text-sm text-muted-foreground">Peggy is creating 3 branded posts. Pete is drafting your first campaign. This takes about 60 seconds.</p>
            <div
              ref={logsRef}
              className="h-32 overflow-y-auto custom-scrollbar bg-black/40 rounded-xl border border-border p-3 text-left font-mono text-[10px]"
              data-testid="samples-log-stream"
            >
              {logs.map((log, i) => <AgentLogLine key={i} log={log} />)}
            </div>
          </div>
        )}

        {/* Step 4: Sample Preview */}
        {wizardStep === "samples" && (
          <div className="space-y-6" data-testid="step-samples">
            <div className="rounded-2xl border border-border bg-card/30 p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <Sparkles size={18} className="text-pink-400" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-lg">Sample Content</h2>
                  <p className="text-sm text-muted-foreground">Here's what Peggy and Pete have prepared. Approve to launch.</p>
                </div>
              </div>

              {plan?.samplePosts && plan.samplePosts.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Sample Posts (by Peggy)</p>
                  <div className="grid gap-4 sm:grid-cols-3">
                    {plan.samplePosts.map((post, i) => (
                      <div key={i} className="space-y-2" data-testid={`sample-post-${i}`}>
                        <div className="w-full">
                          <SocialPreviewMockup
                            imageUrl={post.mediaUrl || undefined}
                            caption={post.caption}
                            platform={mapPlatformToFormat(post.platform)}
                            hashtags={post.hashtags || []}
                            username={businessName}
                            showPlatformToggle={false}
                          />
                        </div>
                        <p className="text-[9px] text-muted-foreground/60 line-clamp-2">{post.caption.slice(0, 80)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {plan?.sampleAds && plan.sampleAds.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Sample Ad Campaign (by Pete)</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {plan.sampleAds.map((ad, i) => (
                      <div key={i} className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-3" data-testid={`sample-ad-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">Pete Draft</span>
                          <span className="text-[10px] text-muted-foreground">{ad.platform}</span>
                        </div>
                        {ad.imageUrl && (
                          <div className="rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }}>
                            <img src={ad.imageUrl} alt="Ad" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-bold text-foreground">{ad.headline}</p>
                          <p className="text-[11px] text-muted-foreground mt-1">{ad.bodyText}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!plan?.samplePosts || plan.samplePosts.length === 0) && (!plan?.sampleAds || plan.sampleAds.length === 0) && (
                <div className="text-center py-8 space-y-3">
                  <p className="text-sm text-muted-foreground">Samples are still being generated...</p>
                  <button onClick={() => refetchPlan()} className="flex items-center gap-1.5 text-blue-400 text-sm mx-auto">
                    <RefreshCw size={13} />
                    Refresh
                  </button>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  data-testid="button-back-to-strategy"
                  onClick={async () => {
                    await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/deep-plan/feedback`, { feedback: "Show me the strategy overview again" });
                    refetchPlan();
                  }}
                  className="px-4 py-3 border border-border text-muted-foreground hover:text-foreground rounded-xl font-semibold flex items-center gap-2 transition-colors text-sm"
                >
                  <ArrowLeft size={14} />
                  Adjust Strategy
                </button>
                <button
                  data-testid="button-lock-and-launch"
                  onClick={() => lockPlan.mutate()}
                  disabled={lockPlan.isPending}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {lockPlan.isPending ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                  {lockPlan.isPending ? "Locking strategy..." : "Lock Strategy & Launch →"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Strategy already exists but no wizard step — show current state */}
        {plan && !["research", "style", "strategy", "generating_samples", "samples"].includes(wizardStep) && wizardStep !== "locked" && (
          <div className="rounded-2xl border border-border bg-card/30 p-6 text-center space-y-4">
            <p className="text-muted-foreground">Your strategy is in progress. Current step: {wizardStep}</p>
            <button
              onClick={() => refetchPlan()}
              className="flex items-center gap-2 text-blue-400 text-sm mx-auto"
            >
              <RefreshCw size={14} />
              Refresh Status
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DeepPlan() {
  return (
    <WizardErrorBoundary>
      <DeepPlanInner />
    </WizardErrorBoundary>
  );
}
